import BannerNotification from '@atoms/BannerNotification/BannerNotification';
import Button from '@atoms/Button/Button';
import Drawer, { IDrawerContentRef } from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import AddToCartErrorEnum from '@models/enums/AddToCartErrorEnum';
import BannerNotificationStylesEnum from '@models/enums/BannerNotificationStylesEnum';
import { IAddToCartTray, IBasketDetails } from '@models/IAddToCartTray';
import { ICartItem } from '@models/ICartItem';
import { ICrossSellTabbedProductsList } from '@models/ICrossSellTabbedProductsList';
import CartItem from '@molecules/CartItem/CartItem';
import TabbedCrossSellProductListing from '@molecules/TabbedCrossSellProductListing/TabbedCrossSellProductListing';
import useCartSizeStore from '@state/CartSizeStore';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import axios, { AxiosError } from 'axios';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import ItemRemovedNotification from './ItemRemovedNotification';

interface Props extends IAddToCartTray {
  readonly isOpen: boolean;
  readonly onSetIsOpen: Dispatch<SetStateAction<boolean>>;
}

const AddToCartTray = ({
  addedItemText,
  addedItemsText,
  apiDownLabel,
  removedItemText,
  removedItemsText,
  basketLink,
  cartRemoveItemLabel,
  checkoutLink,
  changeCartItemEndpoint,
  closeLabel,
  removeItemFromCartEndpoint,
  undoLabel,
  amountSaveLabel,
  hasMaxBasketQuantityWarning,
  maxBasketQuantityWarningNotification,
  maxBasketQuantityErrorNotification,
  maxBasketValueErrorNotification,
  addedItem,
  giftItems,
  giftItemsNotification,
  tabbedProductsList,
  basketProducts,
  totalBasketValue,
  isOpen,
  onSetIsOpen,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const drawerContentRef = useRef<IDrawerContentRef>(null);
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAddedItem, setCurrentAddedItem] = useState<ICartItem>();
  const [isAddedItemRemoved, setIsAddedItemRemoved] = useState(false);
  const [basketDetails, setBasketDetails] = useState<IBasketDetails>({
    basketProducts,
    totalBasketValue,
  });
  const [giftItemsDetails, setGiftItemsDetails] = useState<ICartItem[]>([]);
  const [mainItemsQuantity, setMainItemsQuantity] = useState(1);
  const [crossSellItemsQuantity, setCrossSellItemsQuantity] = useState(0);
  const [
    currentHasMaxBasketQuantityWarning,
    setCurrentHasMaxBasketQuantityWarning,
  ] = useState(!!hasMaxBasketQuantityWarning);
  const [hasMaxBasketQuantityError, setHasMaxBasketQuantityError] =
    useState(false);
  const [hasMaxBasketValueError, setHasMaxBasketValueError] = useState(false);
  const [crossSellTabbedItems, setCrossSellTabbedItems] =
    useState(tabbedProductsList);
  const hasMaxBasketError = hasMaxBasketQuantityError || hasMaxBasketValueError;

  const displayedBannerNotification = (() => {
    if (currentHasMaxBasketQuantityWarning)
      return maxBasketQuantityWarningNotification;
    if (hasMaxBasketQuantityError) return maxBasketQuantityErrorNotification;
    if (hasMaxBasketValueError) return maxBasketValueErrorNotification;
    return null;
  })();

  const totalItems = mainItemsQuantity + crossSellItemsQuantity;
  const addedText = `${totalItems} ${
    totalItems === 1 ? addedItemText : addedItemsText
  }`;
  const removedText = `${mainItemsQuantity} ${
    mainItemsQuantity === 1 ? removedItemText : removedItemsText
  }`;

  const { cartSizeName, setCartSize } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  // Creates a look up obj to handle removal + undo removal of individual gift items
  const [giftItemsRemovalStatuses, setGiftItemsRemovalStatuses] = useState<
    Record<string, boolean>
  >({});

  // This is for when we close the modal and open it back without refreshing the page
  // In this case, the component does not remount and we need to update a couple of things
  useEffect(() => {
    if (isOpen) {
      setBasketDetails({ basketProducts, totalBasketValue });
      setCurrentAddedItem(addedItem);
      setMainItemsQuantity(addedItem.selectedQuantity);
      setCurrentHasMaxBasketQuantityWarning(!!hasMaxBasketQuantityWarning);
      setCrossSellTabbedItems(tabbedProductsList);
      setGiftItemsDetails(giftItems || []);
      setGiftItemsRemovalStatuses(
        giftItems
          ? giftItems.reduce(
              (acc, item) => ({
                ...acc,
                [`${item.code}`]: false,
              }),
              {}
            )
          : {}
      );
    }
  }, [
    isOpen,
    addedItem,
    giftItems,
    hasMaxBasketQuantityWarning,
    basketProducts,
    totalBasketValue,
    tabbedProductsList,
  ]);

  const addToCartErrorHandler = (error: AxiosError) => {
    const errorCode = error.response?.data?.errorCode;

    if (errorCode === AddToCartErrorEnum.maxBasketQuantity) {
      setHasMaxBasketQuantityError(true);
      drawerContentRef.current?.scrollToTop();
    }

    if (errorCode === AddToCartErrorEnum.maxBasketValue) {
      setHasMaxBasketValueError(true);
      drawerContentRef.current?.scrollToTop();
    }
  };

  const handleRemoveItemFromCart = async (code: string, isGift?: boolean) => {
    try {
      setIsLoading(true);

      const { data } = await axios.post<IBasketDetails>(
        removeItemFromCartEndpoint,
        {
          code,
          isGift,
        }
      );

      setBasketDetails(data);
      setMainItemsQuantity(currentAddedItem!.selectedQuantity);
      setIsAddedItemRemoved(true);
      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
      setCurrentHasMaxBasketQuantityWarning(false);
      setHasMaxBasketQuantityError(false);
      setHasMaxBasketValueError(false);
      updateDataLayer({
        event: 'removeFromCart',
        ecommerce: {
          currency: currencyCode,
          items: [
            {
              ...productData,
              quantity: 0,
            },
          ],
          value: data.totalBasketValue,
          quantityChange: -currentAddedItem!.selectedQuantity,
        },
      });
    } catch (error) {
      setHasApiError(true);
      setIsLoading(false);
    }
  };

  const handleRemoveGiftItemFromCart = async (giftItem: ICartItem) => {
    try {
      setIsLoading(true);

      const { data } = await axios.post<IBasketDetails>(
        removeItemFromCartEndpoint,
        {
          code: giftItem.code,
          isGift: true,
        }
      );
      setGiftItemsRemovalStatuses((status) => ({
        ...status,
        [giftItem.code]: true,
      }));
      setBasketDetails(data);
      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
      setCurrentHasMaxBasketQuantityWarning(false);
      setHasMaxBasketQuantityError(false);
      setHasMaxBasketValueError(false);
      updateDataLayer({
        event: 'removeFromCart',
        ecommerce: {
          currency: currencyCode,
          items: [
            {
              ...giftItem,
              quantity: 0,
            },
          ],
          value: data.totalBasketValue,
          quantityChange: -giftItem!.selectedQuantity,
        },
      });
    } catch (error) {
      setHasApiError(true);
      setIsLoading(false);
    }
  };

  const handleChangeCartItemQuantity = async (
    code: string,
    quantity: number
  ) => {
    try {
      setIsLoading(true);

      const { data } = await axios.post<IAddToCartTray>(
        changeCartItemEndpoint,
        { code, quantity }
      );

      setBasketDetails({
        basketProducts: data.basketProducts,
        totalBasketValue: data.totalBasketValue,
      });
      if (data.giftItems?.length) setGiftItemsDetails(data.giftItems);
      setCurrentAddedItem(data.addedItem);
      setMainItemsQuantity(data.addedItem.selectedQuantity);
      setCrossSellTabbedItems(data.tabbedProductsList);
      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
      setHasMaxBasketQuantityError(false);
      setHasMaxBasketValueError(false);
      setCurrentHasMaxBasketQuantityWarning(!!data.hasMaxBasketQuantityWarning);

      if (data.hasMaxBasketQuantityWarning)
        drawerContentRef.current?.scrollToTop();

      if (productData) {
        updateDataLayer({
          event: quantity > 0 ? 'addToCart' : 'removeFromCart',
          ecommerce: {
            currency: currencyCode,
            items: [
              {
                ...productData,
                quantity: data.addedItem.selectedQuantity,
              },
            ],
            value: data.totalBasketValue,
            quantityChange: quantity,
          },
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
      setIsLoading(false);
      setCurrentHasMaxBasketQuantityWarning(false);
      addToCartErrorHandler(error as AxiosError);
    }
  };

  const handleUndoRemoval = async () => {
    try {
      setIsLoading(true);

      const { data } = await axios.post<IAddToCartTray>(
        changeCartItemEndpoint,
        {
          code: currentAddedItem!.code,
          quantity: currentAddedItem!.selectedQuantity,
        }
      );

      setBasketDetails({
        basketProducts: data.basketProducts,
        totalBasketValue: data.totalBasketValue,
      });
      setMainItemsQuantity(currentAddedItem!.selectedQuantity);
      setIsAddedItemRemoved(false);
      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
      setCurrentHasMaxBasketQuantityWarning(!!data.hasMaxBasketQuantityWarning);

      if (productData) {
        updateDataLayer({
          event: 'addToCart',
          ecommerce: {
            currency: currencyCode,
            items: [
              {
                ...productData,
                quantity: data.addedItem.selectedQuantity,
              },
            ],
            value: data.totalBasketValue,
          },
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
      setIsLoading(false);
      setCurrentHasMaxBasketQuantityWarning(false);
      addToCartErrorHandler(error as AxiosError);
    }
  };

  const handleUndoGiftRemoval = async (giftItem: ICartItem) => {
    try {
      setIsLoading(true);

      const { data } = await axios.post<IAddToCartTray>(
        changeCartItemEndpoint,
        {
          code: giftItem.code,
          quantity: giftItem.selectedQuantity,
        }
      );

      setGiftItemsRemovalStatuses((status) => ({
        ...status,
        [giftItem.code]: false,
      }));

      setBasketDetails({
        basketProducts: data.basketProducts,
        totalBasketValue: data.totalBasketValue,
      });
      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
      setCurrentHasMaxBasketQuantityWarning(!!data.hasMaxBasketQuantityWarning);

      updateDataLayer({
        event: 'addToCart',
        ecommerce: {
          currency: currencyCode,
          items: [
            {
              ...giftItem,
              quantity: data.addedItem.selectedQuantity,
            },
          ],
          value: data.totalBasketValue,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
      setIsLoading(false);
      setCurrentHasMaxBasketQuantityWarning(false);
      addToCartErrorHandler(error as AxiosError);
    }
  };

  const updateCrossSellItemDataLayer = (
    currentTabbedProductsList: ICrossSellTabbedProductsList,
    currentTotalBasketValue: number,
    code: string,
    isAddToCartOperation: boolean
  ) => {
    const itemTrackingData = currentTabbedProductsList.tabs
      ?.flatMap((tab) => tab.items)
      .find((item) => item.trackingData?.id === code)?.trackingData;
    if (itemTrackingData)
      updateDataLayer({
        event: isAddToCartOperation ? 'addToCart' : 'removeFromCart',
        contentType: 'slide out tray',
        ecommerce: {
          currency: currencyCode,
          items: [{ ...mapToNewTrackingData(itemTrackingData), quantity: 1 }],
          value: currentTotalBasketValue,
          ...(isAddToCartOperation ? {} : { removedItemId: code }),
        },
      });
  };

  const handleAddCrossSellItemToCart =
    (
      onSuccessfulAddToCart: (
        tabbedProductsList: ICrossSellTabbedProductsList
      ) => void
    ) =>
    async (code: string) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IAddToCartTray>(
          changeCartItemEndpoint,
          {
            code,
            quantity: 1,
            mainItemCode: currentAddedItem?.code,
          }
        );

        onSuccessfulAddToCart(data.tabbedProductsList);
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setBasketDetails({
          basketProducts: data.basketProducts,
          totalBasketValue: data.totalBasketValue,
        });
        setCrossSellItemsQuantity((prev) => prev + 1);
        setCurrentHasMaxBasketQuantityWarning(
          !!data.hasMaxBasketQuantityWarning
        );
        setCrossSellTabbedItems(data.tabbedProductsList);
        updateCrossSellItemDataLayer(
          data.tabbedProductsList,
          data.totalBasketValue,
          code,
          true
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
        addToCartErrorHandler(error as AxiosError);
        setIsLoading(false);
      }
    };

  const handleRemoveCrossSellItemFromCart =
    (onSuccessfulRemoveFromCart: () => void) => async (code: string) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IBasketDetails>(
          removeItemFromCartEndpoint,
          { code }
        );

        onSuccessfulRemoveFromCart();
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setBasketDetails(data);
        setCrossSellItemsQuantity((prev) => prev - 1);
        updateCrossSellItemDataLayer(
          crossSellTabbedItems,
          data.totalBasketValue,
          code,
          false
        );
      } catch (error) {
        setHasApiError(true);
        setIsLoading(false);
      }
    };

  const ctaButtons = (
    <>
      <Button
        link={basketLink}
        secondary
        dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Basket-Link"
        isLightMode
      />

      <Button
        link={checkoutLink}
        dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Checkout-Link"
        linkCallback={() => {
          updateDataLayer({
            event: 'beginCheckout',
            ecommerce: {
              currency: currencyCode,
              items: basketDetails.basketProducts,
              value: basketDetails.totalBasketValue,
            },
          });
        }}
      />
    </>
  );

  return (
    <Drawer
      ref={drawerContentRef}
      isOpen={isOpen && isMobile !== undefined}
      setIsOpen={(value) => {
        setIsAddedItemRemoved(false);
        setMainItemsQuantity(1);
        onSetIsOpen(value);
      }}
      maxWidthClassName="max-w-[946px]"
      contentSpacingClassName="py-7.5 md:pb-6 md:pt-[120px]"
      contentClassName="overflow-y-auto"
      isBottomToTopSliding={isMobile}
      dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray"
    >
      <LoadingOverlay isLoading={isLoading} />
      <AnimatePresence mode="popLayout">
        {!isAddedItemRemoved ? (
          <motion.div
            key="addedItemPresent"
            className="px-5 md:px-13"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <div
              className={classNames(
                'mb-10 hidden flex-wrap items-center gap-5 md:flex',
                hasMaxBasketError ? 'justify-end' : 'justify-between'
              )}
            >
              {!hasMaxBasketError && (
                <div
                  className="flex items-center md:gap-x-2.5"
                  data-testid="CT-149-Add-To-Basket-Slide-Out-Tray-Added-Text"
                >
                  <Icon name="check_circle" colorClassName="text-green-100" />
                  <span className="body-one body-one--bold">{addedText}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
                {ctaButtons}
              </div>
            </div>

            {!hasMaxBasketError && (
              <div
                className="mb-7.5 flex items-center gap-x-3 md:hidden"
                data-testid="CT-149-Add-To-Basket-Slide-Out-Tray-Added-Text"
              >
                <Icon name="check_circle" colorClassName="text-green-100" />
                <span className="body-one body-one--bold">{addedText}</span>
              </div>
            )}

            {displayedBannerNotification && (
              <BannerNotification
                displayStyle={displayedBannerNotification.displayStyle}
                additionalCustomStyles={{
                  [BannerNotificationStylesEnum.Info]: {
                    backgroundClassName: 'bg-white',
                  },
                  [BannerNotificationStylesEnum.Error]: {
                    borderClassName: undefined,
                    backgroundClassName: 'bg-white',
                    paddingClassName: 'p-5',
                    textColorClassName: 'text-red-100',
                  },
                }}
                iconName={displayedBannerNotification.icon}
                richText={displayedBannerNotification.text}
                link={displayedBannerNotification.link}
                className={classNames('mb-7.5 md:mb-10', {
                  'max-md:mt-7.5': hasMaxBasketError,
                })}
              />
            )}
            {currentAddedItem && (
              <div className="flex flex-col gap-7.5">
                {currentAddedItem && (
                  <CartItem
                    {...currentAddedItem}
                    onChangeCartItemQuantity={handleChangeCartItemQuantity}
                    onRemoveItemFromCart={handleRemoveItemFromCart}
                    amountSaveLabel={amountSaveLabel}
                    desktopBreakpoint="md"
                    hasBorder={false}
                    isContainerAnimated={false}
                  />
                )}

                {giftItemsDetails.map((giftItem) => {
                  const giftRemovedText = `${giftItem.selectedQuantity} ${
                    giftItem.selectedQuantity === 1
                      ? removedItemText
                      : removedItemsText
                  }`;

                  return !giftItemsRemovalStatuses[giftItem.code] ? (
                    <CartItem
                      {...giftItem}
                      onRemoveItemFromCart={() =>
                        handleRemoveGiftItemFromCart(giftItem)
                      }
                      amountSaveLabel={amountSaveLabel}
                      desktopBreakpoint="md"
                      hasBorder={false}
                      isContainerAnimated={false}
                      giftNotification={giftItemsNotification}
                      key={giftItem.displayName}
                      isFreeItem
                    />
                  ) : (
                    <ItemRemovedNotification
                      removedText={giftRemovedText}
                      handleUndoRemoval={() => handleUndoGiftRemoval(giftItem)}
                      undoLabel={undoLabel}
                      key={giftItem.displayName}
                    />
                  );
                })}
              </div>
            )}

            <div className="mt-7.5 flex flex-wrap items-center gap-x-5 gap-y-2.5 md:hidden">
              {ctaButtons}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="addedItemRemoved"
            className="px-5 md:px-13"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <div className="mb-10 hidden flex-wrap items-center justify-end gap-x-5 gap-y-2.5 md:flex">
              {ctaButtons}
            </div>
            <ItemRemovedNotification
              removedText={removedText}
              handleUndoRemoval={handleUndoRemoval}
              undoLabel={undoLabel}
            />
            <div className="mt-7.5 flex flex-wrap items-center gap-5 md:hidden">
              {ctaButtons}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!crossSellTabbedItems.tabs?.length && (
          <motion.div
            layout="position"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-15 md:mt-24"
          >
            <TabbedCrossSellProductListing
              itemsPayload={crossSellTabbedItems}
              isLightMode
              carouselHorizontalSpacingClassName="px-5 md:px-13"
              titleClassName="px-5 md:px-13"
              tabsClassName="max-md:pl-5"
              onAddItemToCart={handleAddCrossSellItemToCart}
              onRemoveItemFromCart={handleRemoveCrossSellItemFromCart}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasApiError && (
          <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <BannerNotification
              closeLabel={closeLabel}
              caption={apiDownLabel}
              displayStyle={BannerNotificationStylesEnum.Error}
              iconName="warning"
              className="z-[9999] fixed md:right-2.5 max-w-[650px] right-0 mx-5 bg-white bottom-5"
              title={cartRemoveItemLabel}
              onDismissNotification={() => setHasApiError(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};

export default AddToCartTray;
